import { useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  ArrowLeft,
  ChevronRight,
  CornerDownLeft,
  CalendarClock,
  Check,
  CircleAlert,
  FileText,
  Layers,
  ListChecks,
  Minus,
  Plus,
  Percent,
  Search,
  Sparkles,
  Trophy,
  Upload,
  X,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { StandaloneDatePicker } from '@/components/date-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RichTextEditor } from '@/components/rich-text';
import { Slider } from '@/components/ui/slider';
import {
  useGetPublicSkillFrameworkQuery,
  useGetSkillFrameworkQuery,
} from '@/state/skills/skills-api';
import {
  useCreateAssignmentMutation,
  useCreateCourseStructureMutation,
  useCreateCourseSubStructureMutation,
  useGetCourseSectionsQuery,
  useGetCourseDetailQuery,
  useGetCourseMarkingSchemeQuery,
  useGetCourseTeachersQuery,
  useLazyGenerateAssignmentIdeasQuery,
} from '@/state/course';
import { useGetCurrentUserQuery } from '@/state/user';
import { useClientPermissions } from '@/hooks/use-client-permissions';
import {
  IdeasDrawer,
  type GeneratedIdea,
} from '@/pages/courses/components/add-assignment/ideas-drawer';
import {
  StepHeader,
  type StepMetaItem,
} from '@/pages/courses/components/add-assignment/step-header';
import { StepContainer } from '@/pages/courses/components/add-assignment/step-container';
import {
  buildLexicalDocument,
  createHeadingNode,
  createListNode,
  createParagraphNode,
} from '@/lib/lexical-builders';

const SKILL_LEVEL_LABELS: Record<number, string> = {
  0: 'Unspecified',
  1: 'Supplementary',
  2: 'Beneficial',
  3: 'Significant',
  4: 'Crucial',
  5: 'Essential',
};

type FrameworkSource = 'org' | 'public';

type WizardStep = 0 | 1 | 2 | 3;

interface SkillTreeNode {
  id: number;
  name: string;
  children: SkillTreeNode[];
}

interface FrameworkSelectionItem {
  id: number;
  key: string;
  source: FrameworkSource;
  name: string;
  children: SkillTreeNode[];
}

interface SkillCandidate {
  id: number;
  key: string;
  frameworkKey: string;
  frameworkName: string;
  name: string;
}

interface SelectedSkillItem extends SkillCandidate {
  level: number;
}

const getSkillLevelLabel = (level: number): string =>
  SKILL_LEVEL_LABELS[level] || 'Unspecified';

const flattenFrameworkSkills = (
  frameworkKey: string,
  frameworkName: string,
  node: SkillTreeNode
): SkillCandidate[] => {
  const current: SkillCandidate = {
    id: node.id,
    key: `${frameworkKey}:${node.id}`,
    frameworkKey,
    frameworkName,
    name: node.name,
  };
  const descendants = (node.children || []).flatMap((child) =>
    flattenFrameworkSkills(frameworkKey, frameworkName, child)
  );
  return [current, ...descendants];
};

const hasMeaningfulRichText = (value?: string | null) => {
  if (!value) return false;
  try {
    const parsed = JSON.parse(value);
    const stack = [parsed?.root];
    while (stack.length) {
      const node = stack.pop();
      if (!node) continue;
      if (typeof node.text === 'string' && node.text.trim().length > 0) {
        return true;
      }
      if (Array.isArray(node.children)) {
        stack.push(...node.children);
      }
    }
    return false;
  } catch {
    return value.trim().length > 0;
  }
};

const buildIdeaRichText = (idea: GeneratedIdea) => {
  const nodes = [];
  if (idea.description) {
    nodes.push(createHeadingNode('Description', 'h3'));
    nodes.push(createParagraphNode(idea.description));
  }
  if (idea.learning_objectives?.length) {
    nodes.push(createHeadingNode('Learning Objectives', 'h3'));
    nodes.push(
      createListNode(
        idea.learning_objectives.map((objective) => {
          const label = objective.category
            ? objective.category.toUpperCase()
            : 'OBJECTIVE';
          return `${label}: ${objective.objective}`;
        })
      )
    );
  }
  if (idea.deliverables?.length) {
    nodes.push(createHeadingNode('Suggested Deliverables', 'h3'));
    nodes.push(
      createListNode(
        idea.deliverables.map((item) => {
          const typeLabel = item.type ? ` (${item.type})` : '';
          return `${item.item}${typeLabel}: ${item.description}`;
        })
      )
    );
  }
  if (idea.skills?.length) {
    nodes.push(createHeadingNode('Relevant Skills/Competencies', 'h3'));
    nodes.push(
      createListNode(
        idea.skills.map(
          (skill) => `${skill.skill}${skill.level ? ` — ${skill.level}` : ''}`
        )
      )
    );
  }
  if (idea.suggested_timeline || idea.complexity) {
    nodes.push(createHeadingNode('Timeline & Complexity', 'h3'));
    const items = [];
    if (idea.suggested_timeline) {
      items.push(`Timeline: ${idea.suggested_timeline}`);
    }
    if (idea.complexity) {
      items.push(`Complexity: ${idea.complexity}`);
    }
    if (items.length) {
      nodes.push(createListNode(items));
    }
  }

  return buildLexicalDocument(nodes);
};

const AddAssignmentPage = () => {
  const navigate = useNavigate();
  const { tenant, courseId } = useParams();
  const [searchParams] = useSearchParams();

  const parsedCourseId = Number(courseId);
  const sectionParam = searchParams.get('section');
  const parsedSectionId = sectionParam ? Number(sectionParam) : null;
  const courseSectionId = Number.isFinite(parsedSectionId)
    ? parsedSectionId
    : null;

  const [currentStep, setCurrentStep] = useState<WizardStep>(0);

  const [frameworkTab, setFrameworkTab] = useState<FrameworkSource>('org');
  const [selectedFrameworkKeys, setSelectedFrameworkKeys] = useState<string[]>(
    []
  );

  const [skillSearch, setSkillSearch] = useState('');
  const [selectedSkills, setSelectedSkills] = useState<SelectedSkillItem[]>([]);
  const [draftSkillLevels, setDraftSkillLevels] = useState<
    Record<string, number>
  >({});

  const [title, setTitle] = useState('');
  const [details, setDetails] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [dueDate, setDueDate] = useState<Date | undefined>(undefined);
  const [assignmentType, setAssignmentType] = useState('Individual assignment');
  const [fullMarks, setFullMarks] = useState('100');
  const [weightage, setWeightage] = useState('');

  const [ideasDrawerOpen, setIdeasDrawerOpen] = useState(false);
  const [generatedIdeas, setGeneratedIdeas] = useState<GeneratedIdea[] | null>(
    null
  );
  const [isGeneratingIdeas, setIsGeneratingIdeas] = useState(false);
  const [ideasError, setIdeasError] = useState<string | null>(null);
  const [triggerGenerateIdeas] = useLazyGenerateAssignmentIdeasQuery();

  const serializedDetailsGetterRef = useRef<(() => string) | null>(null);
  const detailsContainerRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const queryArg =
    tenant && Number.isFinite(parsedCourseId)
      ? { tenant, courseId: parsedCourseId }
      : skipToken;

  const { data: course, isLoading: isCourseLoading } =
    useGetCourseDetailQuery(queryArg);
  const { data: courseTeachers, isLoading: isTeachersLoading } =
    useGetCourseTeachersQuery(queryArg);
  const { data: courseSections = [], isLoading: isSectionsLoading } =
    useGetCourseSectionsQuery(queryArg);
  const { data: currentUser, isLoading: isUserLoading } =
    useGetCurrentUserQuery(tenant ? { tenant } : skipToken);
  const { hasAIModule } = useClientPermissions();

  const { data: organizationFrameworksRaw = [] } = useGetSkillFrameworkQuery(
    tenant ? { tenant } : skipToken
  );
  const { data: publicFrameworksRaw = [] } = useGetPublicSkillFrameworkQuery(
    tenant ? { tenant } : skipToken
  );
  const { data: courseMarkingSchemes = [] } = useGetCourseMarkingSchemeQuery(
    tenant && parsedCourseId ? { tenant, courseId: parsedCourseId } : skipToken
  );

  const [createCourseSubStructure, { isLoading: isCreatingSubStructure }] =
    useCreateCourseSubStructureMutation();
  const [createCourseStructure] = useCreateCourseStructureMutation();
  const [createAssignment, { isLoading: isCreatingAssignment }] =
    useCreateAssignmentMutation();

  const canEdit = useMemo(() => {
    if (!course || !currentUser) return false;
    if (course.lecturer?.id === currentUser.id) return true;
    return (
      courseTeachers?.some(
        (teacherMapping) => teacherMapping.teacher?.id === currentUser.id
      ) ?? false
    );
  }, [course, currentUser, courseTeachers]);

  const isPermissionLoading =
    isCourseLoading || isUserLoading || isTeachersLoading || isSectionsLoading;

  const selectedSectionName = useMemo(() => {
    if (!courseSectionId) return null;
    const match = courseSections.find(
      (section) => section.id === courseSectionId
    );
    return match?.section_name ?? null;
  }, [courseSectionId, courseSections]);

  const organizationFrameworks = useMemo<FrameworkSelectionItem[]>(
    () =>
      organizationFrameworksRaw.map((f) => ({
        id: Number(f.id),
        key: `org:${f.id}`,
        source: 'org',
        name: f.name,
        children: f.children as SkillTreeNode[],
      })),
    [organizationFrameworksRaw]
  );

  const publicFrameworks = useMemo<FrameworkSelectionItem[]>(
    () =>
      publicFrameworksRaw.map((f) => ({
        id: Number(f.id),
        key: `public:${f.id}`,
        source: 'public',
        name: f.name,
        children: f.children as SkillTreeNode[],
      })),
    [publicFrameworksRaw]
  );

  const allFrameworks = useMemo(
    () => [...organizationFrameworks, ...publicFrameworks],
    [organizationFrameworks, publicFrameworks]
  );

  const selectedFrameworks = useMemo(
    () => allFrameworks.filter((f) => selectedFrameworkKeys.includes(f.key)),
    [allFrameworks, selectedFrameworkKeys]
  );

  const allSkillCandidates = useMemo(() => {
    return selectedFrameworks.flatMap((framework) =>
      flattenFrameworkSkills(framework.key, framework.name, {
        id: framework.id,
        name: framework.name,
        children: framework.children || [],
      })
    );
  }, [selectedFrameworks]);

  const filteredSkillCandidates = useMemo(() => {
    const q = skillSearch.trim().toLowerCase();
    if (!q) return allSkillCandidates;
    return allSkillCandidates.filter((skill) =>
      skill.name.toLowerCase().includes(q)
    );
  }, [allSkillCandidates, skillSearch]);

  const selectedSkillsMap = useMemo(() => {
    const map = new Map<string, SelectedSkillItem>();
    selectedSkills.forEach((skill) => map.set(skill.key, skill));
    return map;
  }, [selectedSkills]);

  const availableSkillCandidates = useMemo(
    () =>
      filteredSkillCandidates.filter(
        (skill) => !selectedSkillsMap.has(skill.key)
      ),
    [filteredSkillCandidates, selectedSkillsMap]
  );

  const assignmentBaseMarkingScheme = useMemo(
    () =>
      courseMarkingSchemes.find(
        (item) =>
          item.course_structure?.assessment_type === 'Assignment' &&
          item.course_sub_structure === null
      ) ?? null,
    [courseMarkingSchemes]
  );

  const totalSubStructureAssignmentWeightage = useMemo(
    () =>
      courseMarkingSchemes
        .filter(
          (item) =>
            item.course_structure?.assessment_type === 'Assignment' &&
            item.course_sub_structure !== null
        )
        .reduce((sum, item) => sum + Number(item.course_percentage || 0), 0),
    [courseMarkingSchemes]
  );

  const remainingAssignmentWeightage = useMemo(() => {
    const totalAllowed = Number(
      assignmentBaseMarkingScheme?.course_percentage || 0
    );
    const remaining = totalAllowed - totalSubStructureAssignmentWeightage;
    return remaining > 0 ? remaining : 0;
  }, [
    assignmentBaseMarkingScheme?.course_percentage,
    totalSubStructureAssignmentWeightage,
  ]);

  const updateSelectedFrameworks = (key: string) => {
    setSelectedFrameworkKeys((prev) =>
      prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]
    );
  };

  const handleAddSkill = (skill: SkillCandidate) => {
    if (selectedSkillsMap.has(skill.key)) return;
    const level = draftSkillLevels[skill.key] ?? 1;
    setSelectedSkills((prev) => [...prev, { ...skill, level }]);
  };

  const handleRemoveSkill = (key: string) => {
    setSelectedSkills((prev) => prev.filter((skill) => skill.key !== key));
  };

  const handleAttachmentChange = (files: FileList | null) => {
    if (!files) return;
    setAttachments((prev) => [...prev, ...Array.from(files)]);
  };

  const handleGenerateIdeas = async () => {
    if (!tenant || !parsedCourseId) return;
    setIdeasDrawerOpen(true);
    if (generatedIdeas) return;
    setIdeasError(null);
    setIsGeneratingIdeas(true);
    try {
      const data = await triggerGenerateIdeas({
        tenant,
        courseId: parsedCourseId,
        sectionId: courseSectionId,
      }).unwrap();
      setGeneratedIdeas(data.assignments ?? []);
    } catch (error) {
      setIdeasError(
        error instanceof Error ? error.message : 'Failed to generate ideas.'
      );
    } finally {
      setIsGeneratingIdeas(false);
    }
  };

  const handleUseIdea = (idea: GeneratedIdea) => {
    setTitle(idea.title);
    setDetails(buildIdeaRichText(idea));
    requestAnimationFrame(() => {
      const scroller = detailsContainerRef.current?.querySelector(
        '.editor-scroller'
      ) as HTMLElement | null;
      if (scroller) {
        scroller.scrollTop = 0;
      }
    });
    setIdeasDrawerOpen(false);
  };

  const hasDetails = useMemo(() => hasMeaningfulRichText(details), [details]);

  const canProceedDetails = useMemo(() => {
    const cleanTitle = title.trim();
    if (!cleanTitle) return false;
    const hasAttachment = attachments.length > 0;
    return hasDetails || hasAttachment;
  }, [attachments.length, hasDetails, title]);

  const handleSubmit = async () => {
    if (!tenant || !parsedCourseId) {
      toast.error('Missing course context.');
      return;
    }

    const cleanTitle = title.trim();
    if (!cleanTitle) {
      toast.error('Assignment title is required.');
      return;
    }
    if (!dueDate) {
      toast.error('Due date is required.');
      return;
    }

    let resolvedCourseStructureId =
      assignmentBaseMarkingScheme?.course_structure?.id || 0;

    const parsedWeightage = Number(weightage);
    if (
      !Number.isFinite(parsedWeightage) ||
      parsedWeightage < 0 ||
      (resolvedCourseStructureId &&
        parsedWeightage > remainingAssignmentWeightage)
    ) {
      const maxAllowed = resolvedCourseStructureId
        ? remainingAssignmentWeightage
        : 0;
      toast.error(`Weightage must be between 0 and ${maxAllowed.toFixed(2)}.`);
      return;
    }
    if (!resolvedCourseStructureId && parsedWeightage !== 0) {
      toast.error('Weightage must be 0 until assessment structure is defined.');
      return;
    }

    const parsedFullMarks = Number(fullMarks);
    if (!Number.isFinite(parsedFullMarks) || parsedFullMarks < 0) {
      toast.error('Full marks must be a valid non-negative number.');
      return;
    }

    const assignmentBrief =
      serializedDetailsGetterRef.current?.() ||
      details ||
      '{"root":{"children":[{"children":[],"direction":null,"format":"","indent":0,"type":"paragraph","version":1}],"direction":null,"format":"","indent":0,"type":"root","version":1}}';

    const assignmentTypeValue =
      assignmentType === 'Group assignment' ? 'GROUP' : 'INDIVIDUAL';

    if (!canProceedDetails) {
      toast.error('Provide assignment details or attachments.');
      return;
    }

    try {
      if (!resolvedCourseStructureId) {
        const createdStructure = await createCourseStructure({
          tenant,
          data: {
            assessment_type: 'Assignment',
            description: '-',
          },
        }).unwrap();
        resolvedCourseStructureId = createdStructure.id;
      }

      const subStructureId = (
        await createCourseSubStructure({
          tenant,
          data: {
            sub_structure_type: cleanTitle,
            description: '-',
            structure: resolvedCourseStructureId,
          },
        }).unwrap()
      ).id;

      const formData = new FormData();
      formData.append(
        'create_marking_scheme',
        JSON.stringify({
          full_marks: String(parsedFullMarks),
          course_percentage: parsedWeightage,
          course: String(parsedCourseId),
          course_structure: resolvedCourseStructureId,
          marking_structure_name: cleanTitle,
          course_sub_structure: subStructureId,
        })
      );
      formData.append('assignment_name', cleanTitle);
      formData.append('assignment_brief', assignmentBrief);
      formData.append('submission_deadline', dueDate.toISOString());
      formData.append('assignment_type', assignmentTypeValue);
      formData.append(
        'assignments_skills',
        JSON.stringify(
          selectedSkills.map((skill) => ({
            skills: skill.id,
            required_level: skill.level,
          }))
        )
      );
      if (courseSectionId) {
        formData.append('course_section', String(courseSectionId));
      } else {
        formData.append('course_section', '');
      }
      formData.append('course', String(parsedCourseId));
      attachments.forEach((file) => {
        formData.append('assignment_repo', file);
      });

      await createAssignment({ tenant, data: formData }).unwrap();
      toast.success('Assignment created successfully.');

      const params = new URLSearchParams();
      params.set('section', 'assignments');
      if (courseSectionId) {
        params.set('chapter', String(courseSectionId));
        params.set('tab', '4');
      }
      navigate(`/${tenant}/courses/${parsedCourseId}?${params.toString()}`);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to create assignment.'
      );
    }
  };

  const stepMeta: StepMetaItem[] = [
    { title: 'Select frameworks', icon: Layers },
    { title: 'Add skills', icon: ListChecks },
    { title: 'Assignment details', icon: FileText },
    { title: 'Schedule & grading', icon: CalendarClock },
  ];

  const detailsEditor = useMemo(
    () => (
      <RichTextEditor
        value={details}
        onChange={setDetails}
        onProvideGetSerializedState={(getSerializedState) => {
          serializedDetailsGetterRef.current = getSerializedState;
        }}
        placeholder="Assignment details"
        className="[&_.editor-container]:m-0 [&_.editor-container]:h-full [&_.editor-inner]:h-full [&_.editor-scroller]:max-h-[400px] [&_.editor-scroller]:overflow-y-auto [&_.editor-scroller]:pr-2 [&_.editor-scroller]:pt-10 [&_.editor-input]:min-h-[360px]"
        minHeight={0}
      />
    ),
    [details]
  );

  if (isPermissionLoading) {
    return (
      <div className="px-3 py-6 text-sm text-muted-foreground">
        Loading assignment wizard…
      </div>
    );
  }

  if (!canEdit) {
    return (
      <div className="space-y-4 px-3 py-6">
        <div className="rounded-2xl border bg-card p-6 shadow-sm">
          <p className="text-lg font-semibold">Access restricted</p>
          <p className="mt-1 text-sm text-muted-foreground">
            You don’t have permission to add assignments for this course.
          </p>
          <Button
            type="button"
            variant="outline"
            className="mt-4"
            onClick={() => navigate(-1)}
          >
            Go back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-screen-2xl space-y-6 px-3 py-5 sm:px-4 lg:px-8">
      <div className="rounded-2xl border bg-gradient-to-r from-slate-50 via-white to-slate-100/80 p-5 shadow-sm dark:from-slate-950 dark:via-slate-900/80 dark:to-slate-950/80">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
              Assignment builder
            </p>
            <p className="text-lg font-semibold sm:text-2xl">Add assignment</p>
            <p className="text-sm text-muted-foreground">
              Create and publish a new assignment.
            </p>
          </div>
          <Button
            type="button"
            variant="secondary"
            className="inline-flex items-center gap-2"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
        </div>
      </div>

      <StepHeader
        currentStep={currentStep}
        steps={stepMeta}
        courseName={course?.course_name}
        sectionName={selectedSectionName}
      />

      {currentStep === 0 ? (
        <StepContainer key="step-0" animationKey={currentStep}>
          <div className="flex min-h-[640px] flex-col">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-lg font-semibold">Select frameworks</p>
                <p className="text-sm text-muted-foreground">
                  Choose frameworks to pull skills from.
                </p>
              </div>
              <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
                <Button
                  type="button"
                  variant={frameworkTab === 'org' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFrameworkTab('org')}
                  className="flex-1 sm:flex-none"
                >
                  Your organization frameworks
                </Button>
                <Button
                  type="button"
                  variant={frameworkTab === 'public' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFrameworkTab('public')}
                  className="flex-1 sm:flex-none"
                >
                  Public frameworks
                </Button>
              </div>
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {(frameworkTab === 'org'
                ? organizationFrameworks
                : publicFrameworks
              ).map((framework) => {
                const isSelected = selectedFrameworkKeys.includes(
                  framework.key
                );
                return (
                  <button
                    key={framework.key}
                    type="button"
                    className={`rounded-xl border px-4 py-3 text-left transition-all ${
                      isSelected
                        ? 'border-primary/50 bg-primary/5'
                        : 'border-muted'
                    }`}
                    onClick={() => updateSelectedFrameworks(framework.key)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{framework.name}</p>
                        <p className="text-sm text-muted-foreground">
                          Skill/Competence
                        </p>
                      </div>
                      {isSelected ? (
                        <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary text-white">
                          <Check className="h-3 w-3" />
                        </span>
                      ) : (
                        <span className="h-5 w-5 rounded-full border" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="mt-auto flex items-center justify-end gap-2 border-t pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setCurrentStep(1)}
              >
                <Minus className="h-4 w-4 sm:hidden" />
                <span className="hidden items-center gap-2 sm:inline-flex">
                  Skip
                  <Minus className="h-4 w-4" />
                </span>
              </Button>
              <Button type="button" onClick={() => setCurrentStep(1)}>
                <ChevronRight className="h-4 w-4 sm:hidden" />
                <span className="hidden items-center gap-2 sm:inline-flex">
                  Continue
                  <ChevronRight className="h-4 w-4" />
                </span>
              </Button>
            </div>
          </div>
        </StepContainer>
      ) : null}

      {currentStep === 1 ? (
        <StepContainer key="step-1" animationKey={currentStep}>
          <div className="flex min-h-[640px] flex-col">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-lg font-semibold">Add skills</p>
                <p className="text-sm text-muted-foreground">
                  Search and add skills from selected frameworks. This step is
                  optional.
                </p>
              </div>
            </div>

            <div className="mt-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={skillSearch}
                  onChange={(event) => setSkillSearch(event.target.value)}
                  placeholder="Search skill"
                  className="pl-9"
                />
              </div>

              <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
                <div className="rounded-lg border bg-background/60 p-4">
                  <p className="text-sm text-muted-foreground">
                    Results: {availableSkillCandidates.length}
                  </p>
                  <div className="mt-3 max-h-[420px] space-y-4 overflow-y-scroll overscroll-contain">
                    {availableSkillCandidates.map((skill) => {
                      const alreadySelected = selectedSkillsMap.has(skill.key);
                      const level = draftSkillLevels[skill.key] ?? 1;
                      return (
                        <div
                          key={skill.key}
                          className="rounded-lg border bg-card p-3"
                        >
                          <div className="flex items-center justify-between">
                            <p className="font-medium">{skill.name}</p>
                            <span className="text-xs text-muted-foreground">
                              {getSkillLevelLabel(level)}
                            </span>
                          </div>
                          <Slider
                            value={[level]}
                            min={0}
                            max={5}
                            step={1}
                            onValueChange={(value) =>
                              setDraftSkillLevels((prev) => ({
                                ...prev,
                                [skill.key]: value[0],
                              }))
                            }
                          />
                          <div className="mt-2 flex justify-end">
                            <Button
                              type="button"
                              // size="icon"
                              variant="outline"
                              onClick={() => handleAddSkill(skill)}
                              disabled={alreadySelected}
                            >
                              <Plus className="h-4 w-4" /> Add
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="rounded-lg border bg-background/60 p-4">
                  <p className="text-sm text-muted-foreground">
                    Selected skills: {selectedSkills.length}
                  </p>
                  <div className="mt-3 max-h-[420px] space-y-3 overflow-y-scroll overscroll-contain">
                    {selectedSkills.map((skill) => (
                      <div
                        key={skill.key}
                        className="rounded-lg border bg-card px-3 py-2"
                      >
                        <div className="flex items-center justify-between">
                          <p className="font-medium">{skill.name}</p>
                          <span className="text-xs text-muted-foreground">
                            {getSkillLevelLabel(skill.level)}
                          </span>
                        </div>
                        <div className="mt-2 flex items-center justify-between gap-2">
                          <div className="h-2 flex-1 rounded-full bg-muted">
                            <div
                              className="h-2 rounded-full bg-primary"
                              style={{ width: `${(skill.level / 5) * 100}%` }}
                            />
                          </div>
                          <button
                            type="button"
                            className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-red-50 text-red-500 transition hover:bg-red-100 dark:bg-red-500/10 dark:text-red-300"
                            onClick={() => handleRemoveSkill(skill.key)}
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-auto flex flex-wrap items-center justify-between gap-2 border-t pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setCurrentStep(0)}
              >
                <CornerDownLeft className="h-4 w-4 sm:hidden" />
                <span className="hidden items-center gap-2 sm:inline-flex">
                  <CornerDownLeft className="h-4 w-4" />
                  Back
                </span>
              </Button>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setCurrentStep(2)}
                >
                  <Minus className="h-4 w-4 sm:hidden" />
                  <span className="hidden items-center gap-2 sm:inline-flex">
                    Skip
                    <Minus className="h-4 w-4" />
                  </span>
                </Button>
                <Button type="button" onClick={() => setCurrentStep(2)}>
                  <ChevronRight className="h-4 w-4 sm:hidden" />
                  <span className="hidden items-center gap-2 sm:inline-flex">
                    Continue
                    <ChevronRight className="h-4 w-4" />
                  </span>
                </Button>
              </div>
            </div>
          </div>
        </StepContainer>
      ) : null}

      {currentStep === 2 ? (
        <StepContainer key="step-2" animationKey={currentStep}>
          <div className="flex min-h-[640px] flex-col">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-lg font-semibold">Assignment details</p>
                <p className="text-sm text-muted-foreground">
                  Add assignment description and attachments. Title is required.
                </p>
              </div>
              {hasAIModule ? (
                <Button
                  type="button"
                  variant="secondary"
                  className="inline-flex items-center gap-2"
                  onClick={handleGenerateIdeas}
                >
                  <Sparkles className="h-4 w-4" />
                  Generate idea
                </Button>
              ) : null}
            </div>

            <div className="mt-4 space-y-4">
              <div className="space-y-1">
                <label className="text-sm font-medium">Title</label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Title"
                />
              </div>

              <div className="space-y-1">
                <label className="text-sm font-medium">
                  Assignment details
                </label>
                <div
                  ref={detailsContainerRef}
                  className="relative z-0 rounded-xl border bg-background/60 p-1"
                >
                  {detailsEditor}
                </div>
                <div className="mt-2 inline-flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50/70 px-3 py-2 text-xs text-blue-700 dark:border-blue-800/60 dark:bg-slate-900/70 dark:text-blue-300">
                  <CircleAlert className="mt-0.5 h-4 w-4" />
                  Note: Please include the evaluation criteria in the assignment
                  details. It will be used for grading by AI if you have access
                  to the AI module.
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Attachments</label>
                <div className="rounded-xl border border-dashed bg-muted/30 p-6 text-center">
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={(event) =>
                      handleAttachmentChange(event.target.files)
                    }
                  />
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 rounded-md border bg-background px-3 py-2 text-sm"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload className="h-4 w-4" />
                    Upload files
                  </button>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Supported files type are PDF | PNG | JPEG.
                  </p>
                  {attachments.length > 0 ? (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {attachments.map((file, idx) => (
                        <span
                          key={`${file.name}-${idx}`}
                          className="rounded-md border bg-background px-2 py-1 text-xs"
                        >
                          {file.name}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="mt-auto flex flex-wrap items-center justify-between gap-2 border-t pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setCurrentStep(1)}
              >
                <CornerDownLeft className="h-4 w-4 sm:hidden" />
                <span className="hidden items-center gap-2 sm:inline-flex">
                  <CornerDownLeft className="h-4 w-4" />
                  Back
                </span>
              </Button>
              <Button
                type="button"
                onClick={() => setCurrentStep(3)}
                disabled={!canProceedDetails}
              >
                <ChevronRight className="h-4 w-4 sm:hidden" />
                <span className="hidden items-center gap-2 sm:inline-flex">
                  Continue
                  <ChevronRight className="h-4 w-4" />
                </span>
              </Button>
            </div>
          </div>
        </StepContainer>
      ) : null}

      {currentStep === 3 ? (
        <StepContainer key="step-3" animationKey={currentStep}>
          <div className="flex min-h-[640px] flex-col">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-lg font-semibold">Schedule & grading</p>
                <p className="text-sm text-muted-foreground">
                  Finalize assignment settings.
                </p>
              </div>
            </div>

            <div className="mt-4 space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <label className="inline-flex items-center gap-2 text-sm font-medium">
                    <CalendarClock className="h-4 w-4 text-muted-foreground" />
                    Due date
                  </label>
                  <StandaloneDatePicker value={dueDate} onChange={setDueDate} />
                </div>
                <div className="space-y-2">
                  <label className="inline-flex items-center gap-2 text-sm font-medium">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    Assignment type
                  </label>
                  <Select
                    value={assignmentType}
                    onValueChange={setAssignmentType}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Individual assignment">
                        Individual assignment
                      </SelectItem>
                      <SelectItem value="Group assignment">
                        Group assignment
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-1">
                  <label className="inline-flex items-center gap-2 text-sm font-medium">
                    <Trophy className="h-4 w-4 text-muted-foreground" />
                    Full marks
                  </label>
                  <Input
                    type="number"
                    value={fullMarks}
                    onChange={(e) => setFullMarks(e.target.value)}
                    className="w-full"
                  />
                </div>
                <div className="space-y-1">
                  <label className="inline-flex items-center gap-2 text-sm font-medium">
                    <Percent className="h-4 w-4 text-muted-foreground" />
                    Weightage (%)
                  </label>
                  <Input
                    type="number"
                    value={weightage}
                    onChange={(e) => setWeightage(e.target.value)}
                    className="w-full"
                  />
                  <p className="text-xs text-muted-foreground">
                    Remaining: {remainingAssignmentWeightage.toFixed(2)}%
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-auto flex flex-wrap items-center justify-between gap-2 border-t pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setCurrentStep(2)}
              >
                <CornerDownLeft className="h-4 w-4 sm:hidden" />
                <span className="hidden items-center gap-2 sm:inline-flex">
                  <CornerDownLeft className="h-4 w-4" />
                  Back
                </span>
              </Button>
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={isCreatingAssignment || isCreatingSubStructure}
              >
                <ChevronRight className="h-4 w-4 sm:hidden" />
                <span className="hidden items-center gap-2 sm:inline-flex">
                  Create assignment
                  <ChevronRight className="h-4 w-4" />
                </span>
              </Button>
            </div>
          </div>
        </StepContainer>
      ) : null}

      <div className="h-2" />

      <IdeasDrawer
        open={ideasDrawerOpen}
        onOpenChange={setIdeasDrawerOpen}
        ideas={generatedIdeas}
        isLoading={isGeneratingIdeas}
        error={ideasError}
        onUseIdea={handleUseIdea}
      />
    </div>
  );
};

export default AddAssignmentPage;
